import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SITE_URL } from '../../constants';

const SITE_NAME = 'Visit All Nepal';
const DEFAULT_TITLE = 'Visit All Nepal';
const DEFAULT_DESCRIPTION = 'Visit All Nepal — your Nepal travel guide with guided treks, destinations, visa help, permits, festivals, and step-by-step journeys across Nepal.';
const DEFAULT_KEYWORDS = 'visit all nepal, nepal, nepal travel, visit nepal, travel nepal, nepal tourism, nepal trekking, nepal guide, kathmandu, pokhara, everest base camp, annapurna, nepal festivals, nepal visa';
const DEFAULT_IMAGE = `${SITE_URL}/og-cover.jpg`;

/**
 * Per-route SEO tags (title, description, canonical, Open Graph, Twitter Card, JSON-LD).
 * Renders via react-helmet-async so tags update on client-side route changes, which
 * index.html's static <head> block (the fallback for non-JS crawlers) cannot do.
 *
 * @param {string} title - Page title, site name is appended automatically.
 * @param {string} titleExact - Full document title as-is (homepage / brand pages).
 * @param {string} description - Meta description (~150-160 chars ideal).
 * @param {string} path - Route path used to build the canonical URL, e.g. '/packages'.
 * @param {string} [image] - Absolute OG image URL; defaults to the site's cover image.
 * @param {string} [keywords] - Comma-separated keywords for legacy crawlers.
 * @param {boolean} [noindex] - Set true for auth/utility pages that shouldn't be indexed.
 * @param {object|object[]} [schema] - JSON-LD object(s) to embed.
 */
function Seo({ title, titleExact, description, path = '', image, keywords, noindex, schema }) {
  const fullTitle = titleExact || (title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE);
  const desc = description || DEFAULT_DESCRIPTION;
  const canonical = `${SITE_URL}${path}`;
  const ogImage = image || DEFAULT_IMAGE;
  const schemaList = schema ? (Array.isArray(schema) ? schema : [schema]) : [];

  return (
    // defer={false}: write tags synchronously instead of waiting on requestAnimationFrame.
    // Some headless crawlers/prerenderers throttle rAF in background tabs, which would
    // otherwise delay or drop these tags for exactly the audience this component serves.
    <Helmet defer={false}>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <meta name="keywords" content={keywords || DEFAULT_KEYWORDS} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      <meta property="og:type" content="website" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={SITE_NAME} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {schemaList.map((entry, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(entry)}</script>
      ))}
    </Helmet>
  );
}

export default Seo;
